import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import { FilterResult } from '../components/odata-filter/models/filter-result';
import {
  FilterGroup,
  ODataFilter,
  ODataFilterValues,
} from '../components/odata-filter/models/odata-filter';
import { FilterOperator } from '../components/odata-filter/models/operators';
import { SelectedFilter } from '../components/odata-filter/models/selected-filter';
import { ApiVersions } from './api-version';
import { BaseService } from './base.service';
import buildQuery, { Filter } from 'odata-query';
import { CustomView } from '../components/odata-filter/models/custom-view';
import { SavedFilter } from '../components/odata-filter/models/saved-filter';
import { ViewContext } from '../components/odata-filter/models/view-context';
import { CustomViewService } from './custom-view.service';
import { InjectorModule } from '../modules/injector.module';
import { AttributeService } from './attribute.service';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { SearchValueType } from '../components/odata-filter/models/search-value-type';
import { FilterNameChangeMapper } from '../components/odata-filter/models/filter-name-change-mapper';
import { CustomFieldFilterModel } from '../odata-filter-models/custom-field-filter-model';
import { ChildFieldsFilterModel } from '../odata-filter-models/child-fields-filter-model';
import { WorkspaceService } from './workspace.service';
import { ComplexFieldsFilterModel } from '../odata-filter-models/complex-fields-filter-model';

@Injectable({
  providedIn: 'root',
})
export class BaseODataService extends BaseService {
  protected _baseUrl: string;
  protected _searchTextModel: ODataFilter;
  protected _filterResult: FilterResult;
  protected _chartResult: FilterResult;
  protected _filterCollection: ODataFilter[] = [];
  protected _appliedFilters: SelectedFilter[] = [];
  protected _selectedView: CustomView;
  protected _viewContext: ViewContext;
  protected _quickFilterNames: string[] = [];
  protected expand: any = [];
  private _previousLink: string = '';
  private _defaultPageSize: number = 50;
  private _orderBy: string[] = [];
  public searchText: string;
  public readonly filterResultValues = new Subject<any[]>();
  public readonly filterCollection = new BehaviorSubject<ODataFilter[]>(this._filterCollection);
  public readonly appliedFilters = new BehaviorSubject<SelectedFilter[]>(null);
  public readonly chartResultValues = new Subject<any[]>();
  public readonly quickFilterNames = new BehaviorSubject<string[]>(this._quickFilterNames);
  public isExporting = false;

  public isFetchingData = true;

  public set orderBy(value: string[]) {
    this.setOrderBy(value);
  }
  public get orderBy(): string[] {
    return this._orderBy;
  }
  protected setOrderBy(value: string[]) {
    this._orderBy = value;
  }
  public get totalResultCount(): number {
    return this._filterResult.count;
  }
  public get filterResult(): FilterResult {
    return this._filterResult;
  }
  protected get filterUrl() {
    return `query/${this._baseUrl}`;
  }
  protected get allResultsFilterUrl() {
    const urlParms = this._baseUrl.split('/');
    if (urlParms.length > 1) {
      return `query/${urlParms[0]}MaxResult/${urlParms.filter((x) => x != urlParms[0]).join('/')}`;
    }
    return `query/${this._baseUrl}MaxResult`;
  }

  get selectedView(): CustomView {
    return this._selectedView;
  }
  protected customViewService: CustomViewService;
  protected attributeService: AttributeService;

  protected workspaceService: WorkspaceService;
  private _currentODataFilterQuery: string;

  public get currentODataFilterQuery(): string {
    return this._currentODataFilterQuery;
  }

  constructor() {
    super();
    this.customViewService = InjectorModule.injector.get(CustomViewService);
    this.attributeService = InjectorModule.injector.get(AttributeService);
    this.workspaceService = InjectorModule.injector.get(WorkspaceService);

    //removes the deleted attribute from filter collection
    this.attributeService.attributeDeleted.subscribe((att: AttributeDefinition) => {
      this.removeAttributeFilter(att);
    });

    //removes the unfiltarebale attribute from filter collection
    this.attributeService.unfilterableAttribute.subscribe((att: AttributeDefinition) => {
      this.removeAttributeFilter(att);
    });
  }

  private removeAttributeFilter(att: AttributeDefinition) {
    const index = this._filterCollection.findIndex((x) => x.targetFieldId == att.id);
    if (index != -1) {
      this._filterCollection.splice(index, 1);
      this.filterCollection.next(this._filterCollection);
    }
  }

  public get hasCertMetricsIntegration(): boolean {
    return this.appConfigService.tenant.usesCertifications;
  }

  getChartResult(select: string[], expand: string[]) {
    let filter = this.buildFilter(this._appliedFilters);

    let filterQueryString = buildQuery<any>({
      select: select,
      filter,
      expand: expand,
    });
    let path = this.allResultsFilterUrl.concat(filterQueryString);
    this.get(path, true, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value) {
            return this._chartResult.parse(response);
          } else {
            return null;
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this._chartResult = data;
          this.chartResultValues.next(this._chartResult.values);
        }
      });
  }

  applyFilter(appliedFilters: SelectedFilter[]) {
    if (appliedFilters) {
      this.attributeService.attributes.subscribe((attributes) => {
        if (attributes != null) {
          this._appliedFilters = [...this.validateFilters(attributes, appliedFilters)];
          this.appliedFilters.next(this._appliedFilters);
        }
      });
    }

    // Moved logic into getODataEndpoint - DRY - which will allow to separate path from execution (filtering)
    const path = this.getODataEndpoint(appliedFilters, '', ApiVersions.unversioned);

    this._currentODataFilterQuery = path;
    this.filter(path);
  }

  gotoNextPage() {
    if (!this._filterResult.nextLink) return;
    let path = this._filterResult.nextLink.replace(this.getBaseApiUrl(ApiVersions.v1_0), '');
    this.filter(path, false);
  }
  gotoPreviousPage() {
    if (!this._previousLink) return;
    this.filter(this._previousLink, false);
  }

  applyView(view: CustomView) {
    if (!view) return;
    this.isFetchingData = true;
    this.selectView(view);
    this._quickFilterNames = view.quickFilterNames.map((x) =>
      new FilterNameChangeMapper(view.viewContext).map(x)
    );
    let savedAppiedFilters = SavedFilter.parseToAppliedFilters(view.filters, view.viewContext);
    this.applyFilter(savedAppiedFilters);
    this.quickFilterNames.next(this._quickFilterNames);
  }

  selectView(view: CustomView) {
    this.customViewService.selectView(view);
    this._selectedView = view;
  }

  saveView() {
    let view = new CustomView(this.selectedView);
    view.isDefault = this.selectedView.isDefault;
    view.filters = this._appliedFilters.map((x) =>
      SavedFilter.parseToString(new SelectedFilter(x))
    );
    view.quickFilterNames = this._quickFilterNames;
    this.customViewService.update(view);
  }

  applyManageFilterResultToView(result: string[]) {
    this._quickFilterNames = result;
    this.selectedView.isModified = true;
    this.quickFilterNames.next(this._quickFilterNames);
  }

  setExpandValues(expand: string[] | any) {
    this.expand = expand;
  }

  getODataEndpoint(
    filters: SelectedFilter[],
    pathPrefix: string = '',
    apiVersion: ApiVersions = ApiVersions.unversioned
  ): string {
    const filter = this.buildFilter(filters);
    let filterQueryString = buildQuery({
      count: true,
      filter,
      orderBy: this.orderBy,
      expand: this.expand,
    });

    const path =
      (pathPrefix !== null && pathPrefix.trim().length > 0 ? pathPrefix + '/' : '') +
      (apiVersion !== null && apiVersion !== ApiVersions.unversioned ? apiVersion + '/' : '') +
      this.filterUrl.concat(filterQueryString);

    return path;
  }

  public getFilterQueryForAutomation(appliedFilters: SelectedFilter[]) {
    const filter = appliedFilters.map((x) =>
      x.filter.buildFilter(
        x.searchValues.length > 0 ? x.searchValues : x.searchValue,
        x.filterOperator,
        x.getSubQueryFilters(true),
        true
      )
    );
    return buildQuery({
      count: true,
      filter,
    });
  }

  protected getODataQuery(
    path: string,
    params: ODataQueryParams,
    apiVersion: ApiVersions = ApiVersions.v0_1
  ) {
    let filterQueryString = buildQuery<any>(params);
    let url = this.getBaseApiUrl(apiVersion).concat(path.concat(filterQueryString));
    return url;
  }

  protected filter(path: string, resetResultCount = true) {
    if (resetResultCount) {
      this._filterResult.count = null;
    }

    this.get(path, true, ApiVersions.v1_0)
      .pipe(
        map((response) => {
          if (response && response.value) {
            return this._filterResult.parse(response);
          } else {
            return null;
          }
        })
      )
      .subscribe((data) => {
        this.isFetchingData = false;
        if (data) {
          this.calculatePreviousLink(path);
          this._filterResult = data;
          this.filterResultValues.next(this._filterResult.values);
        }
      });
  }

  private calculatePreviousLink(path: string) {
    let curentSkipNumber = +path
      .substring(path.lastIndexOf('&$skip='), path.length)
      .replace('&$skip=', '');
    let previousSkipNumber = curentSkipNumber - this._defaultPageSize;
    if (previousSkipNumber >= this._defaultPageSize) {
      this._previousLink = path.replace(
        `&$skip=${curentSkipNumber}`,
        `&$skip=${previousSkipNumber}`
      );
    } else {
      this._previousLink = path.replace(`&$skip=${curentSkipNumber}`, '');
    }
  }

  protected buildFilter(appliedFilters: SelectedFilter[]) {
    let filters = appliedFilters.slice();
    if (this.searchText) {
      var searchTextFilter = this.getSearchTextFilter(this.searchText);
      filters.push(searchTextFilter);
    }

    return filters.map((x) =>
      x.filter.buildFilter(
        x.searchValues.length > 0 ? x.searchValues : x.searchValue,
        x.filterOperator,
        x.getSubQueryFilters(false)
      )
    );
  }

  protected addFilterToCurrentFilters(newFilters: any[]) {
    return this.buildFilter([...this._appliedFilters]).concat(newFilters);
  }

  protected systemFilterIndex(filter: ODataFilter) {
    return this._filterCollection.findIndex(
      (x) => x.displayName == filter.displayName && x.filterGroup == filter.filterGroup
    );
  }
  private getSearchTextFilter(searchText: string) {
    return new SelectedFilter({
      filter: this._searchTextModel,
      filterOperator: new FilterOperator(
        'contains',
        FilterOperatorType.contains,
        FilterOperatorType.contains,
        true
      ),
      searchValue: searchText,
    });
  }

  validateFilters(attributes: AttributeDefinition[], appliedFilters: SelectedFilter[]) {
    return appliedFilters.map((appliedFilter) => {
      let filter = appliedFilter.filter;
      if (
        filter.filterGroup == FilterGroup.TeamCustomFields ||
        filter.filterGroup == FilterGroup.PersonCustomFields ||
        filter.filterGroup == FilterGroup.TrackedFields
      ) {
        //checks for deleted customfield
        if (attributes.findIndex((x) => x.id == filter.targetFieldId) == -1) {
          appliedFilter.showFieldDeletedWarning = true;
          return appliedFilter;
        }

        //checks for deleted or changed customfield valueList ita
        if (filter.valueType == SearchValueType.MultiValue) {
          filter.values = attributes
            .find((x) => x.id == filter.targetFieldId)
            .valueList.map((v) => new ODataFilterValues(v.id, v.itemValue));
          appliedFilter.searchValues.forEach((value) => {
            let valuelist = attributes
              .map((x) => {
                return x.valueList.filter((x) => x.id == value.searchValue);
              })
              .filter((x) => x.length);
            if (!valuelist.length) {
              appliedFilter.showFieldDeletedWarning = true;
              return appliedFilter;
            }
          });
        }
      }
      return appliedFilter;
    });
  }
  protected getODataResult(
    path: string,
    params: Partial<ODataQueryParams>,
    apiVersion: ApiVersions
  ) {
    let filterQueryString = buildQuery<any>({
      select: params.select,
      orderBy: params.orderBy,
      expand: params.expand,
      count: params.count,
      filter: params.filter,
    });
    let url = path.concat(filterQueryString);
    return this.get(url, true, apiVersion).pipe(
      map((response) => {
        if (response && response.value) {
          return response;
        } else {
          return null;
        }
      })
    );
  }

  protected getODataResultCountOnly(path: string, filter: Filter, apiVersion: ApiVersions) {
    let filterQueryString = `/$count${buildQuery<any>({ filter: filter })}`;
    let url = path.concat(filterQueryString);
    return this.get(url, true, apiVersion);
  }

  protected setCustomFieldFilterCollection(
    customFieldFilterModel:
      | CustomFieldFilterModel
      | ChildFieldsFilterModel
      | ComplexFieldsFilterModel
  ) {
    let index = this._filterCollection.findIndex(
      (x) => x.targetFieldId == customFieldFilterModel.targetFieldId
    );
    if (index == -1) {
      this._filterCollection.push(customFieldFilterModel);
    } else {
      this._filterCollection[index] = customFieldFilterModel;
    }
  }
}

export interface ODataQueryParams {
  select: string[];
  filter: Filter;
  orderBy: string[];
  expand: string[];
  count: boolean;
}
